import React, { useState } from 'react';
import { Button, Input, Textarea } from '@material-tailwind/react';
import { SaveIcon, SnowflakeIcon } from 'lucide-react';
import Modal from '../../../components/modal-x';
import { useUnfreezeBooking, useUpdateFreezeDates } from '../../../custom-hooks/use-bookings';
import moment from 'moment';

const BookingFreezeManageModal = ({ isOpen, onClose, booking, onSuccess }) => {
    const freeze = booking?.freeze || {};
    const bookingEnd = booking?.endDate ? moment(booking.endDate).format('YYYY-MM-DD') : '';

    const [freezeStartDate, setFreezeStartDate] = useState(
        freeze.freezeStartDate ? moment(freeze.freezeStartDate).format('YYYY-MM-DD') : ''
    );
    const [freezeEndDate, setFreezeEndDate] = useState(
        freeze.freezeEndDate ? moment(freeze.freezeEndDate).format('YYYY-MM-DD') : ''
    );
    const [reason, setReason] = useState(freeze.freezeReason || '');
    const [error, setError] = useState(null);

    const updateDatesMutation = useUpdateFreezeDates();
    const unfreezeMutation = useUnfreezeBooking();

    const handleSaveDates = async (e) => {
        e.preventDefault();
        setError(null);

        if (!freezeStartDate || !freezeEndDate) {
            setError('Both dates are required.');
            return;
        }
        if (moment(freezeStartDate).isSameOrAfter(moment(freezeEndDate), 'day')) {
            setError('Freeze start date must be before the end date.');
            return;
        }
        if (bookingEnd && moment(freezeEndDate).isAfter(moment(bookingEnd), 'day')) {
            setError('Freeze end date cannot be after the booking end date.');
            return;
        }

        try {
            await updateDatesMutation.mutateAsync({
                bookingId: booking._id,
                data: { freezeStartDate, freezeEndDate, reason },
            });
            onSuccess?.();
            onClose();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to update freeze dates.');
        }
    };

    const handleUnfreeze = async () => {
        try {
            await unfreezeMutation.mutateAsync(booking._id);
            onSuccess?.();
            onClose();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to unfreeze booking.');
        }
    };

    return (
        <Modal title="Manage Frozen Booking" isOpen={isOpen} onClose={onClose}>
            <div className="flex flex-col gap-5">
                <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3 text-sm text-cyan-800 flex items-center gap-2">
                    <SnowflakeIcon className="w-4 h-4 flex-shrink-0" />
                    Currently frozen {moment(freeze.freezeStartDate).format('MMM D, YYYY')} – {moment(freeze.freezeEndDate).format('MMM D, YYYY')}
                </div>

                <form onSubmit={handleSaveDates} className="flex flex-col gap-3">
                    <p className="font-semibold text-sm text-gray-800">Change Freeze Dates</p>
                    <Input
                        label="Freeze Start Date"
                        type="date"
                        required
                        value={freezeStartDate}
                        max={bookingEnd || undefined}
                        onChange={(e) => setFreezeStartDate(e.target.value)}
                    />
                    <Input
                        label="Freeze End Date"
                        type="date"
                        required
                        value={freezeEndDate}
                        max={bookingEnd || undefined}
                        onChange={(e) => setFreezeEndDate(e.target.value)}
                    />
                    <Textarea
                        label="Reason for Freeze"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                    />
                    <Button
                        className="w-full rounded-full flex items-center justify-center gap-2"
                        variant="filled"
                        color="blue"
                        type="submit"
                        disabled={updateDatesMutation.isLoading}
                        loading={updateDatesMutation.isLoading}
                    >
                        <SaveIcon className="w-4 h-4" />
                        Save Changes
                    </Button>
                </form>

                <hr className="border-gray-200" />

                <div className="flex flex-col gap-2">
                    <p className="font-semibold text-sm text-gray-800">Unfreeze</p>
                    <p className="text-xs text-gray-500">
                        Ends the freeze immediately and resumes billing for this booking from today,
                        instead of the original end date above.
                    </p>
                    <Button
                        className="w-full rounded-full flex items-center justify-center gap-2"
                        variant="outlined"
                        color="orange"
                        onClick={handleUnfreeze}
                        disabled={unfreezeMutation.isLoading}
                        loading={unfreezeMutation.isLoading}
                    >
                        Unfreeze Booking
                    </Button>
                </div>

                {error && <p className="text-red-500 text-sm">{error}</p>}
            </div>
        </Modal>
    );
};

export default BookingFreezeManageModal;
